import type { VolumeReport } from '#shared/types';
import { Heatmap, LineChart, type Series } from '#client/components/charts';
import { Card } from '#client/components/ui/Card';
import { BreakdownChartCard, BreakdownTableCard } from './BreakdownCards';
import { SectionShell } from './SectionShell';
import { formatterFor } from './format';
import { comparisonLabel, dimensionLabel, granularityLabel, sumValues } from './report.logic';
import { toCompareSeries, withComparison } from './to-series';
import { useAnalyticsFilters } from './useAnalyticsFilters';
import { useAnalyticsSection } from './useAnalyticsSection';

/**
 * How much work came in, and of what kind.
 *
 * The breakdown switches shape with the dimension, because the shapes answer
 * different questions: a ranking wants bars, a mix wants a ring, and a
 * time-of-day pattern wants neither.
 */
export function VolumePage() {
  const filters = useAnalyticsFilters('volume');
  const { data, loading, error } = useAnalyticsSection<VolumeReport>('volume', filters);

  return (
    <SectionShell
      section="volume"
      filters={filters}
      data={data}
      loading={loading}
      error={error}
      metrics={data?.headline}
      empty={data !== undefined && data.breakdown.length === 0}
    >
      {data && <VolumeGrid report={data} />}
    </SectionShell>
  );
}

function VolumeGrid({ report }: { report: VolumeReport }) {
  const compare = toCompareSeries(report.series, comparisonLabel(report.compare));
  const label = dimensionLabel(report.by);

  return (
    <div className="c-an__grid">
      <BookedCard report={report} compare={compare} />
      <BreakdownChartCard
        label={label}
        breakdown={report.breakdown}
        centre={{ value: String(sumValues(report.breakdown)), label: 'tests' }}
        seriesLabel="Booked"
      />
      {report.heatmap && <ArrivalsCard heatmap={report.heatmap} />}
      <BreakdownTableCard
        label={label}
        breakdown={report.breakdown}
        countHeader="Booked"
        secondaryHeader="Value booked"
      />
    </div>
  );
}

function BookedCard({
  report,
  compare,
}: {
  report: Pick<VolumeReport, 'series' | 'granularity'>;
  compare: Series | null;
}) {
  return (
    <Card
      className="c-an__cell c-an__cell--wide"
      title="Tests booked"
      subtitle={`${granularityLabel(report.granularity)}, by registration date`}
    >
      <LineChart
        title="Tests booked over time"
        series={withComparison(report.series, compare)}
        area={compare === null}
        format={formatterFor('count')}
        height={260}
      />
    </Card>
  );
}

function ArrivalsCard({ heatmap }: { heatmap: NonNullable<VolumeReport['heatmap']> }) {
  return (
    <Card
      className="c-an__cell c-an__cell--wide"
      title="When the work arrives"
      subtitle="Registrations by hour and weekday — the pattern staffing has to match"
    >
      <Heatmap
        title="Registrations by hour and weekday"
        rows={heatmap.rows}
        columns={heatmap.columns}
        cells={heatmap.cells}
        format={formatterFor('count')}
        columnStride={3}
      />
    </Card>
  );
}
